import {API} from 'aws-amplify'
import {apiCallWithAuthCheck} from '../auth/core/AuthErrorInterceptor'

const apiName: string = process.env.REACT_APP_API_NAME || 'JobService'

export interface MembershipSubscription {
  subscription_id: string
  skey: string
  role: 'owner' | 'admin' | 'member'
  product: string | null
  max_seats: number
  used_seats: number
  /** True for the subscription currently linked on the user's account (USER#/USER# row). */
  is_primary: boolean
  /** True when Susy reports IsEvaluation (trial license). */
  is_evaluation: boolean
  /** Susy IsEnabled flag. `null` means the Susy lookup failed (treated as unknown). */
  is_enabled: boolean | null
  /** Susy-computed end date (YYYY-MM-DD), or null if unknown. */
  end_date: string | null
  /** True if end_date has passed. Expired non-primary entries are filtered out server-side. */
  is_expired: boolean
}

export interface UserSubscriptionsResponse {
  subscriptions: MembershipSubscription[]
}

export interface Member {
  sub: string
  name: string
  email: string
  role: 'owner' | 'admin' | 'member'
  status: 'active' | 'revoked'
  joined_at: string
  invited_at: string
  units_consumed: number
  units_limit?: number
}

export interface ListMembersResponse {
  members: Member[]
  /**
   * Seat cap from Susy. Populated by older backend builds; current builds omit
   * this field because the frontend already has the same value from the Susy-
   * backed `/v1/user/subscriptions` payload (via `SubscriptionProvider.maxSeats`
   * / `subscriptionDetails.maxHosts`). Treat as optional — `ManageUsersSection`
   * falls back to the SubscriptionProvider values when this is undefined.
   */
  max_seats?: number
  used_seats: number
}

export interface InviteResponse {
  status: string
  token: string
}

export interface AcceptInvitationResponse {
  status: string
  subscription_id: string
}

export interface RemoveMemberResponse {
  status: string
}

export interface UpdateRoleResponse {
  status: string
  role: 'admin' | 'member'
}

export interface UpdateUnitsResponse {
  status: string
  units_limit: number | null
}

export interface ResetUnitsResponse {
  status: string
  units_consumed: number
}

/**
 * One row from the discoverable[] list returned by /subscription-discovery.
 *
 * The backend already filters expired and disabled entries out of this list,
 * so every row the frontend receives is safe to either auto-link or surface to
 * the user. Entries with `is_enabled === null` represent keys where the Susy
 * lookup failed — we keep them so a flaky upstream call does not swallow real
 * subscriptions. `/associate2` will reject them at link time if they turn out
 * to be invalid.
 */
export interface DiscoveredSubscription {
  subscription_id: string
  skey: string
  name: string | null
  allow_autolink: boolean
  product_name: string | null
  is_enabled: boolean | null
  end_date: string | null
  is_expired: boolean
}

export interface DiscoveryLinkedEntry {
  subscription_id: string
  skey: string
  is_primary: boolean
}

export interface SubscriptionDiscoveryResponse {
  email: string
  linked: DiscoveryLinkedEntry[]
  discoverable: DiscoveredSubscription[]
  /**
   * Optional non-fatal warnings. Known values:
   *   - "subscription_lookup_unavailable" — the upstream subscription lookup
   *     was unreachable; `discoverable[]` will be empty but the user might
   *     actually have new keys. Surface with neutral wording (never mention
   *     the upstream system by name to end users).
   */
  warnings?: string[]
}

export const getSubscriptionDiscovery = async (): Promise<SubscriptionDiscoveryResponse> => {
  return API.get(apiName, 'subscription-discovery', {})
}

export const getUserSubscriptions = async (): Promise<UserSubscriptionsResponse> => {
  return API.get(apiName, 'v1/user/subscriptions', {})
}

export const listMembers = async (subscriptionId: string): Promise<ListMembersResponse> => {
  return apiCallWithAuthCheck(async () => {
    return API.get(apiName, `v1/subscription/${subscriptionId}/members`, {})
  })
}

export const inviteMember = async (subscriptionId: string, email: string): Promise<InviteResponse> => {
  return apiCallWithAuthCheck(async () => {
    return API.post(apiName, `v1/subscription/${subscriptionId}/invite`, {
      body: {email},
    })
  })
}

export const acceptInvitation = async (token: string): Promise<AcceptInvitationResponse> => {
  return apiCallWithAuthCheck(async () => {
    return API.post(apiName, `v1/invitation/${token}/accept`, {body: {}})
  })
}

export const removeMember = async (
  subscriptionId: string,
  memberSub: string
): Promise<RemoveMemberResponse> => {
  return apiCallWithAuthCheck(async () => {
    return API.del(apiName, `v1/subscription/${subscriptionId}/members/${memberSub}`, {})
  })
}

export const updateMemberRole = async (
  subscriptionId: string,
  memberSub: string,
  role: 'admin' | 'member'
): Promise<UpdateRoleResponse> => {
  return apiCallWithAuthCheck(async () => {
    return API.patch(apiName, `v1/subscription/${subscriptionId}/members/${memberSub}/role`, {
      body: {role},
    })
  })
}

export const updateMemberUnitsLimit = async (
  subscriptionId: string,
  memberSub: string,
  unitsLimit: number | null
): Promise<UpdateUnitsResponse> => {
  return apiCallWithAuthCheck(async () => {
    return API.patch(apiName, `v1/subscription/${subscriptionId}/members/${memberSub}/units`, {
      body: {units_limit: unitsLimit},
    })
  })
}

export const resetMemberUnits = async (
  subscriptionId: string,
  memberSub: string
): Promise<ResetUnitsResponse> => {
  return apiCallWithAuthCheck(async () => {
    return API.post(apiName, `v1/subscription/${subscriptionId}/members/${memberSub}/reset-units`, {
      body: {},
    })
  })
}
